import React, { useMemo } from 'react';
import { SxProps } from '@mui/system';
import { Box } from '@mui/material';
import SplineChartV2, { SplineChartV2Series } from 'src/components/BeaconV2Components/SplineChartV2/SplineChartV2';
import { useComparisonWeekIds } from 'src/components/BeaconV2Components/SplineChartV2/useComparisonWeekIds';
import { getLastWeekIdOfYear, yearPartOfWeekId } from 'src/utils/dateUtils';
import { METRICTYPE } from 'src/utils/entityDefinitions';

export function SlCardContainer({ children, sx }: { children: React.ReactNode; sx?: SxProps }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        ...sx
      }}
    >
      {children}
    </Box>
  );
}

export function SlCardHeader({ children, sx }: { children: React.ReactNode; sx?: SxProps }) {
  return (
    <Box
      sx={{
        paddingTop: '16px',
        paddingLeft: '16px',
        paddingRight: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        ...sx
      }}
    >
      {children}
    </Box>
  );
}

export function SlCardFooter({ children, sx }: { children: React.ReactNode; sx?: SxProps }) {
  return (
    <>
      <div style={{ flex: 1 }}></div>
      <Box
        sx={{
          paddingLeft: '16px',
          paddingRight: '16px',
          paddingBottom: '16px',
          ...sx
        }}
      >
        {children}
      </Box>
    </>
  );
}

export function SlCardTitle({ children, sx }: { children: React.ReactNode; sx?: SxProps }) {
  return (
    <Box
      sx={{
        fontSize: '16px',
        fontWeight: 500,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        ...sx
      }}
    >
      {children}
    </Box>
  );
}

/**
 * Data point for SlCardSplineChart using weekId-based format
 */
export interface SlCardSplineChartDataPoint {
  weekId: number;
  value: number;
}

export function SlCardSplineChart({
  metricType,
  primaryData,
  secondaryData,
  width,
  height = 64,
  sx
}: {
  metricType: METRICTYPE;
  primaryData: SlCardSplineChartDataPoint[];
  secondaryData?: SlCardSplineChartDataPoint[];
  width: number;
  height: number;
  sx?: SxProps;
}) {
  // Get comparison week IDs to detect YTD + Prior Year mode
  const { isYTDWithPriorYear, comparisonStartWeekId, comparisonEndWeekId } = useComparisonWeekIds();

  // For YTD + Prior Year, expand categories to include full prior year range
  // This ensures the comparison series shows all of last year's data
  const categoryOverrides = useMemo(() => {
    if (isYTDWithPriorYear && comparisonStartWeekId && comparisonEndWeekId) {
      // Use the comparison period's start and the last week of that year as the category range
      const comparisonYear = yearPartOfWeekId(comparisonStartWeekId);
      return {
        categoryStartWeekId: comparisonStartWeekId,
        categoryEndWeekId: getLastWeekIdOfYear(comparisonYear)
      };
    }
    return {};
  }, [isYTDWithPriorYear, comparisonStartWeekId, comparisonEndWeekId]);

  const primarySeries: SplineChartV2Series = useMemo(
    () => ({
      id: 'primary',
      name: 'Primary',
      data: (primaryData || [])
        .map((data) => ({
          weekId: data.weekId,
          value: data.value
        }))
        .sort((a, b) => a.weekId - b.weekId)
    }),
    [primaryData]
  );

  const comparisonSeries: SplineChartV2Series | undefined = useMemo(() => {
    if (!secondaryData || secondaryData.length === 0) {
      return undefined;
    }
    return {
      id: 'comparison',
      name: 'Comparison',
      data: secondaryData
        .map((data) => ({
          weekId: data.weekId,
          value: data.value
        }))
        .sort((a, b) => a.weekId - b.weekId)
    };
  }, [secondaryData]);

  return (
    <Box
      sx={{
        marginTop: '92px',
        ...sx
      }}
    >
      <SplineChartV2
        preview={{ width, height }}
        primarySeries={primarySeries}
        comparisonSeries={comparisonSeries}
        metricType={metricType}
        removeSpacing
        {...categoryOverrides}
        chartOptionsOverride={{
          chart: {
            marginLeft: -2
          },
          plotOptions: {
            series: {
              lineWidth: 2
            }
          }
        }}
      />
    </Box>
  );
}

export function SlCardBack({ children, sx }: { children: React.ReactNode; sx?: SxProps }) {
  return (
    <Box
      sx={{
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        ...sx
      }}
    >
      {children}
    </Box>
  );
}
